/**
 * AiSidebarContext - React context for managing the AI sidebar state and actions.
 *
 * Provides sidebar width, open/close state, and current AI action (flashcards, quiz, summary, chat, topics).
 * Used to coordinate the AI sidebar panel in the document editor.
 */
import { createContext, useContext, useState } from "react";

interface AiSidebarContextType {
  sidebarWidth: number;
  setSidebarWidth: (width: number) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  currentAction: 'flashcards' | 'quiz' | 'summary' | 'topics' | 'chat' | null;
  setCurrentAction: (action: 'flashcards' | 'quiz' | 'summary' | 'topics' | 'chat' | null) => void;
}

const AiSidebarContext = createContext<AiSidebarContextType | undefined>(undefined);

export const AiSidebarProvider = ({ children }: { children: React.ReactNode }) => {
  const [sidebarWidth, setSidebarWidth] = useState(500);
  const [isOpen, setIsOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState<'flashcards' | 'quiz' | 'summary' | 'topics' | 'chat' | null>(null);

  return (
    <AiSidebarContext.Provider value={{
      sidebarWidth,
      setSidebarWidth,
      isOpen,
      setIsOpen,
      currentAction,
      setCurrentAction,
    }}>
      {children}
    </AiSidebarContext.Provider>
  );
};

export const useAiSidebar = () => {
  const context = useContext(AiSidebarContext);
  if (context === undefined) {
    throw new Error("useAiSidebar must be used within an AiSidebarProvider");
  }
  return context
}; 